<div class="relative w-full overflow-hidden" [style.marginTop.px]="headerOffset" [style.height]="'calc(100vh - ' + headerOffset + 'px)'">
  <div class="relative">
    <div
      #container
  class="border border-gray-300 rounded bg-white relative mx-auto box-border"
      [style.width]="'100%'"
      [style.height]="'100%'"
      [style.minHeight.px]="400"
      [class.opacity-0]="isLoading"
      [class.opacity-100]="!isLoading"
      [class.transition-opacity]="true"
      [class.duration-500]="true"
    ></div>

    <!-- Loading overlay -->
    @if(isLoading){
    <div
      class="absolute inset-0 flex items-center justify-center bg-gray-100 rounded"
    >
      <div class="text-center max-w-md px-4">
        <div
          class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"
        ></div>
        <p class="text-gray-700 font-semibold text-lg mb-2">{{"NETWORK_ANALYSIS.LOADING_GRAPH" | translate}}...</p>
        <p class="text-gray-500 text-sm mb-3">{{"NETWORK_ANALYSIS.QUERY_MAY_TAKE_TIME" | translate}}</p>
        @if(currentQueryDescription) {
          <div class="bg-white border border-gray-300 rounded-lg p-3 shadow-sm">
            <div class="text-blue-700 font-medium text-sm mb-1 flex items-center justify-center">
              <svg class="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
                <path d="M7 8h10M7 12h6"/>
              </svg>
              {{"NETWORK_ANALYSIS.RUNNING_QUERY" | translate}}:
            </div>
            <p class="text-sm text-gray-700 font-medium">{{ currentQueryDescription | translate }}</p>
          </div>
        }
      </div>
    </div>
    }

    <!-- Empty state overlay when no data -->
    @if(!isLoading && (!graphData || graphData.nodes.length === 0) && !showQueryBuilder){
    <div
      class="absolute inset-0 flex items-center justify-center bg-gray-50 rounded"
    >
      <div class="text-center max-w-lg px-4">
        <svg class="w-16 h-16 text-gray-400 mx-auto mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 1v6m0 6v6"/>
          <path d="m9 9 3 3 3-3"/>
        </svg>
        <h3 class="text-xl font-semibold text-gray-700 mb-2">{{"NETWORK_ANALYSIS.NO_GRAPH_DATA" | translate}}</h3>
        <p class="text-gray-500 mb-4">{{"NETWORK_ANALYSIS.CREATE_YOUR_FIRST_QUERY" | translate}}</p>
        <button 
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          (click)="showSplashScreen = true"
        >
          {{"NETWORK_ANALYSIS.OPEN_NETWORK_TOOLS" | translate}}
        </button>
      </div>
    </div>
    }

    <!-- Top toolbar overlay -->
    <div
      class="absolute left-3 right-3 z-30"
      [style.top.px]="8"
      [class.opacity-0]="isLoading"
      [class.opacity-100]="!isLoading"
      [class.transition-opacity]="true"
    >
      <div class="w-full flex items-center justify-between bg-white/95 border border-gray-300 rounded shadow-sm px-1 py-1">
        <!-- Left: icon-only controls: zoom/reset/pin + label toggles (monochrome) -->
        <div class="flex items-center gap-1">
          <!-- Open Query Builder -->
          <button type="button" class="w-8 h-8 grid place-items-center text-gray-800 hover:bg-gray-100 rounded"
                  (click)="openQueryBuilder()" [pTooltip]="'NETWORK_ANALYSIS.OPEN_QUERY_BUILDER' | translate" tooltipPosition="bottom">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
              <path d="M7 8h10M7 12h6"/>
            </svg>
          </button>
          <!-- Open Entity Selector -->
          @if (config.features?.enableEntitySelector !== false) {
          <button type="button" class="w-8 h-8 grid place-items-center text-gray-800 hover:bg-gray-100 rounded"
                  (click)="showSplashScreen = true" [pTooltip]="(config.labels?.entitySelectorLabel || config.primaryEntityDisplayName || 'Entities') | translate" tooltipPosition="bottom">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              <line x1="6.5" y1="17.5" x2="6.51" y2="17.5"/>
              <line x1="6.5" y1="6.5" x2="6.51" y2="6.5"/>
            </svg>
          </button>
          }
          <!-- Zoom in -->
          <button type="button" class="w-8 h-8 grid place-items-center text-gray-800 hover:bg-gray-100 rounded" (click)="zoomIn()" [pTooltip]="'NETWORK_ANALYSIS.ZOOM_IN' | translate" tooltipPosition="bottom">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="7"/>
              <line x1="11" y1="8" x2="11" y2="14"/>
              <line x1="8" y1="11" x2="14" y2="11"/>
              <line x1="16.5" y1="16.5" x2="21" y2="21"/>
            </svg>
          </button>
          <!-- Pin nodes toggle -->
          <button type="button"
            class="w-8 h-8 grid place-items-center rounded text-gray-800 hover:bg-gray-100"
            [class.bg-gray-200]="pinNodesAfterDrag" [class.ring-1]="pinNodesAfterDrag" [class.ring-gray-400]="pinNodesAfterDrag"
            (click)="pinNodesAfterDrag = !pinNodesAfterDrag; onPinNodesChange()"
            [attr.aria-pressed]="pinNodesAfterDrag"
            [pTooltip]="(pinNodesAfterDrag ? 'NETWORK_ANALYSIS.UNPIN_AFTER_DRAG' : 'NETWORK_ANALYSIS.PIN_AFTER_DRAG') | translate" tooltipPosition="bottom"
          >
            <!-- Pin icon (pinned) -->
            <svg [class.hidden]="!pinNodesAfterDrag" viewBox="0 0 24 24" width="16" height="16" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 17v5m-3-2l3-3 3 3M9 2v7.586a2 2 0 0 1-.586 1.414l-2.828 2.828A2 2 0 0 0 5 15.242V19h14v-3.758a2 2 0 0 0-.586-1.414l-2.828-2.828A2 2 0 0 1 15 9.586V2H9z"/>
            </svg>
            <!-- Pin icon (unpinned/outline) -->
            <svg [class.hidden]="pinNodesAfterDrag" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 17v5m-3-2l3-3 3 3M9 2v7.586a2 2 0 0 1-.586 1.414l-2.828 2.828A2 2 0 0 0 5 15.242V19h14v-3.758a2 2 0 0 0-.586-1.414l-2.828-2.828A2 2 0 0 1 15 9.586V2H9z"/>
            </svg>
          </button>
          <!-- Show node labels toggle -->
          <button type="button"
            class="w-8 h-8 grid place-items-center rounded text-gray-800 hover:bg-gray-100"
            [class.bg-gray-200]="showLabels" [class.ring-1]="showLabels" [class.ring-gray-400]="showLabels"
            (click)="showLabels = !showLabels; onShowLabelsChange()"
            [attr.aria-pressed]="showLabels"
            [pTooltip]="(showLabels ? 'NETWORK_ANALYSIS.HIDE_NODE_LABELS' : 'NETWORK_ANALYSIS.SHOW_NODE_LABELS') | translate" tooltipPosition="bottom"
          >
            <!-- Tag icon (on) -->
            <svg [class.hidden]="!showLabels" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10L12 2H4v8l8 8 8-8z"/>
              <circle cx="7" cy="7" r="1.5"/>
            </svg>
            <!-- Tag with slash (off) -->
            <svg [class.hidden]="showLabels" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 10L12 2H4v8l8 8 8-8z"/>
              <circle cx="7" cy="7" r="1.5"/>
              <line x1="3" y1="21" x2="21" y2="3"/>
            </svg>
          </button>
          <!-- Show link labels toggle -->
          <button type="button"
            class="w-8 h-8 grid place-items-center rounded text-gray-800 hover:bg-gray-100"
            [class.bg-gray-200]="showLinkLabels" [class.ring-1]="showLinkLabels" [class.ring-gray-400]="showLinkLabels"
            (click)="showLinkLabels = !showLinkLabels; onShowLinkLabelsChange()"
            [attr.aria-pressed]="showLinkLabels"
            [pTooltip]="(showLinkLabels ? 'NETWORK_ANALYSIS.HIDE_LINK_LABELS' : 'NETWORK_ANALYSIS.SHOW_LINK_LABELS') | translate" tooltipPosition="bottom"
          >
            <!-- Chain icon (on) -->
            <svg [class.hidden]="!showLinkLabels" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"/>
              <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"/>
            </svg>
            <!-- Chain with slash (off) -->
            <svg [class.hidden]="showLinkLabels" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"/>
              <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"/>
              <line x1="3" y1="21" x2="21" y2="3"/>
            </svg>
          </button>
          <!-- Toggle Graph Info window -->
          <button type="button"
            class="w-8 h-8 grid place-items-center rounded text-gray-800 hover:bg-gray-100"
            [class.bg-gray-200]="showGraphInfoPanel" [class.ring-1]="showGraphInfoPanel" [class.ring-gray-400]="showGraphInfoPanel"
            (click)="toggleInfoPanel()"
            [pTooltip]="(showGraphInfoPanel ? 'NETWORK_ANALYSIS.HIDE_GRAPH_INFO' : 'NETWORK_ANALYSIS.SHOW_GRAPH_INFO') | translate" tooltipPosition="bottom"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="8"/>
              <path d="M10 12h2v4"/>
            </svg>
          </button>

          <!-- Toggle Selected panel -->
          <button type="button"
            class="w-8 h-8 grid place-items-center rounded text-gray-800 hover:bg-gray-100"
            [class.bg-gray-200]="showSelectedPanel" [class.ring-1]="showSelectedPanel" [class.ring-gray-400]="showSelectedPanel"
            (click)="showSelectedPanel = !showSelectedPanel"
            [pTooltip]="(showSelectedPanel ? 'NETWORK_ANALYSIS.HIDE_SELECTED_DETAILS' : 'NETWORK_ANALYSIS.SHOW_SELECTED_DETAILS') | translate" tooltipPosition="bottom"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
              <line x1="7" y1="8" x2="17" y2="8"/>
              <line x1="7" y1="12" x2="13" y2="12"/>
            </svg>
          </button>
        </div>

        <!-- Right: Search + weight mode + settings (icons-only toolbar allows input here) -->
        <div class="flex items-center gap-2">
          <div class="relative w-[320px] md:w-[420px]">
            <input
              type="text"
              [(ngModel)]="searchQuery"
              (input)="onSearchInput()"
              (keydown.enter)="searchAndFocus()"
              [placeholder]="'NETWORK_ANALYSIS.SEARCH_NODES_BY_NAME' | translate"
              class="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button type="button"
              (click)="searchAndFocus()"
              class="absolute right-2 top-1.5 text-gray-600 hover:text-gray-900"
              [pTooltip]="'NETWORK_ANALYSIS.SEARCH' | translate" tooltipPosition="bottom"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="7"/>
                <line x1="16.5" y1="16.5" x2="21" y2="21"/>
              </svg>
            </button>

            <!-- Search Results dropdown -->
            @if (searchResults.length > 0) {
              <div class="absolute mt-1 left-0 right-0 bg-white border border-gray-300 rounded max-h-48 overflow-y-auto z-50">
                @for (result of searchResults; track result.id) {
                  <div 
                    class="px-3 py-2 text-xs cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                    (click)="focusOnNode(result)"
                    [class.bg-blue-100]="result === selectedSearchResult"
                  >
                    <div class="font-medium">{{ result.name }}</div>
                    <div class="text-gray-500">{{ result.group }}</div>
                  </div>
                }
              </div>
            }
            @if (searchQuery && searchResults.length === 0) {
              <div class="absolute mt-1 left-0 right-0 bg-white border border-gray-300 rounded p-2 text-xs text-gray-500 italic z-50">{{"NETWORK_ANALYSIS.NO_NODES_FOUND" | translate}}</div>
            }
          </div>
          <div class="hidden md:flex items-center gap-1">
            <button type="button"
              class="px-2 py-1 rounded border text-[10px] font-semibold"
              [class.bg-blue-600]="weightMode === 'frequency'"
              [class.text-white]="weightMode === 'frequency'"
              [class.border-blue-600]="weightMode === 'frequency'"
              [class.text-gray-700]="weightMode !== 'frequency'"
              [class.border-gray-300]="weightMode !== 'frequency'"
              (click)="onWeightModeChange('frequency')">
              {{"NETWORK_ANALYSIS.FREQUENCY_WEIGHT" | translate}}
            </button>
            <button type="button"
              class="px-2 py-1 rounded border text-[10px] font-semibold"
              [class.bg-blue-600]="weightMode === 'cost'"
              [class.text-white]="weightMode === 'cost'"
              [class.border-blue-600]="weightMode === 'cost'"
              [class.text-gray-700]="weightMode !== 'cost'"
              [class.border-gray-300]="weightMode !== 'cost'"
              (click)="onWeightModeChange('cost')">
              {{"NETWORK_ANALYSIS.COST_WEIGHT" | translate}}
            </button>
          </div>
          <button type="button"
            class="w-8 h-8 grid place-items-center text-gray-800 hover:bg-gray-100 rounded border border-gray-300"
            [disabled]="isLoading"
            (click)="toggleSettings()"
            [pTooltip]="'NETWORK_ANALYSIS.OPEN_SETTINGS' | translate" tooltipPosition="bottom"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .67.39 1.27 1 1.51H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          <button type="button"
            class="w-8 h-8 grid place-items-center text-gray-800 hover:bg-gray-100 rounded border border-gray-300"
            (click)="toggleHelp()"
            [pTooltip]="'NETWORK_ANALYSIS.HELP' | translate" tooltipPosition="bottom"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.82 1c0 2-3 2-3 4" />
              <line x1="12" y1="17" x2="12" y2="17" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Splash Screen Modal -->
    @if (showSplashScreen) {
      <div class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 overflow-auto">
        <div class="w-full max-w-6xl mx-4 my-8 bg-white rounded-xl shadow-2xl border border-gray-200">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-2xl font-bold text-gray-900">{{"NETWORK_ANALYSIS.NETWORK_ANALYSIS" | translate}}</h2>
                <p class="text-gray-600 mt-1">{{"NETWORK_ANALYSIS.NETWORK_ANALYSIS_DETAIL" | translate}}</p>
              </div>
              <button 
                type="button" 
                class="text-gray-400 hover:text-gray-600 p-1"
                (click)="closeSplashScreen()"
              >
                <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>

          <!-- Content: Providers dropdown replaces presets -->
          <div class="p-6 overflow-visible">
            <div class="grid md:grid-cols-3 gap-6">
              <!-- Build Custom Query -->
              <div class="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer group overflow-visible" (click)="openQueryBuilder()">
                <div class="flex items-center mb-4">
                  <div class="bg-blue-600 text-white p-3 rounded-lg mr-4 flex-shrink-0">
                    <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900">{{"NETWORK_ANALYSIS.BUILD_YOUR_OWN_QUERY" | translate}}</h3>
                </div>
                <p class="text-sm text-gray-600 mb-4">{{"NETWORK_ANALYSIS.BUILD_YOUR_OWN_QUERY_TEXT" | translate}}.</p>
                <div class="text-blue-600 text-sm font-medium flex items-center">
                  {{"NETWORK_ANALYSIS.GET_STARTED" | translate}}
                  <svg class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>

              <!-- Primary Entity quick start (Provider/Adjuster/etc) -->
              @if (config.features?.enableEntitySelector !== false) {
              <div class="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6 overflow-visible">
                <div class="flex items-center mb-4">
                  <div class="bg-green-600 text-white p-3 rounded-lg mr-4 flex-shrink-0">
                    <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900">{{ config.primaryEntityDisplayName || 'Entities' }}</h3>
                </div>
                <p class="text-sm text-gray-600 mb-4">{{ 'NETWORK_ANALYSIS.SELECT_ENTITY_TO_EXPLORE' | translate }}</p>
                <div class="relative">
                  <select class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                          [ngModel]="selectedProvider" (ngModelChange)="onSelectProvider($event)">
                    <option [ngValue]="null">-- {{ 'NETWORK_ANALYSIS.CHOOSE_ENTITY' | translate }} --</option>
                    @for (p of mainProviders; track p.id) { <option [ngValue]="p">{{ p.name }}</option> }
                  </select>
                </div>
              </div>
              }

              <!-- Top 20 Entities -->
              <div class="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6 overflow-visible">
                <div class="flex items-center mb-4">
                  <div class="bg-purple-600 text-white p-3 rounded-lg mr-4 flex-shrink-0">
                    <svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z"/>
                    </svg>
                  </div>
                  <h3 class="text-lg font-semibold text-gray-900">{{"NETWORK_ANALYSIS.TOP_20" | translate}}</h3>
                </div>
                <p class="text-sm text-gray-600 mb-4">{{"NETWORK_ANALYSIS.TOP_20_DESC" | translate}}</p>
                <div class="relative">
                  <select class="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                          [ngModel]="selectedTop20Entity" (ngModelChange)="onSelectTop20Entity($event)">
                    <option [ngValue]="null">-- {{"NETWORK_ANALYSIS.CHOOSE_ENTITY" | translate}} --</option>
                    @for (actor of config.actors; track actor.label) {
                      <option [value]="actor.label">{{ actor.displayName }}</option>
                    }
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    }
    
    <!-- Query Builder Modal -->
    @if (showQueryBuilder) {
      <div class="fixed inset-0 z-[10000] flex items-start justify-center bg-black/30">
        <div class="mt-10 w-full max-w-4xl bg-white rounded shadow-xl border border-gray-300">
          <div class="flex items-center justify-between px-4 py-3 border-b">
            <div class="font-semibold">{{"NETWORK_ANALYSIS.QUERY_BUILDER" | translate}}</div>
            <div class="flex items-center gap-2">
              <button type="button" class="px-2 py-1 text-xs rounded border" [class.bg-gray-200]="builderTab==='simple'" (click)="onBuilderTabChange('simple')">{{"NETWORK_ANALYSIS.SIMPLE" | translate}}</button>
              <button type="button" class="px-2 py-1 text-xs rounded border" [class.bg-gray-200]="builderTab==='advanced'" (click)="onBuilderTabChange('advanced')">{{"NETWORK_ANALYSIS.ADVANCED" | translate}}</button>
              <button type="button" class="w-8 h-8 grid place-items-center rounded hover:bg-gray-100" (click)="closeQueryBuilder()" [pTooltip]="'NETWORK_ANALYSIS.CLOSE' | translate" tooltipPosition="left">✕</button>
            </div>
          </div>
          <div class="p-4 space-y-3">
            @if (builderTab === 'simple') {
              <!-- Welcome message for first-time users -->
              @if(!cypher || cypher.trim().length === 0) {
                <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div class="flex items-center mb-2">
                    <svg class="w-5 h-5 text-blue-600 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22,4 12,14.01 9,11.01"/>
                    </svg>
                    <h3 class="font-semibold text-blue-900">{{"NETWORK_ANALYSIS.WELCOME_TO_THE_QUERY_BUILDER" | translate}}!</h3>
                  </div>
                  <p class="text-sm text-blue-800">{{"NETWORK_ANALYSIS.BUILD_YOUR_FIRST_GRAPH" | translate}}.</p>
                </div>
              }
              <!-- Simple form-based builder -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div class="md:col-span-2 space-y-3">
                  <div>
                    <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.MODE" | translate}}</label>
                    <select class="w-full border border-gray-300 rounded px-2 py-1 text-sm" [(ngModel)]="builderMode" (change)="onSimpleFieldChange()">
                      <option value="neighborhood">{{"NETWORK_ANALYSIS.NEIGHBORHOOD_AROUND_A_NODE" | translate}}</option>
                    </select>
                  </div>

                  <!-- Neighborhood -->
                  @if (builderMode==='neighborhood') {
                    <div class="grid grid-cols-2 gap-3">
                      <div class="col-span-2 flex items-center gap-3">
                        <label class="text-xs text-gray-600">{{"NETWORK_ANALYSIS.MATCH_BY" | translate}}</label>
                        <label class="text-xs flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="nbKey" [value]="false" [(ngModel)]="builder.neighborhood.useName" (change)="onSimpleFieldChange()" /> {{"NETWORK_ANALYSIS.ID" | translate}}
                        </label>
                        <label class="text-xs flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="nbKey" [value]="true" [(ngModel)]="builder.neighborhood.useName" (change)="onSimpleFieldChange()" /> {{"NETWORK_ANALYSIS.NAME" | translate}}
                        </label>
                      </div>
                      <div class="col-span-2">
                        <label class="block text-xs text-gray-600 mb-1">Entity Type</label>
                        <select class="w-full border rounded px-2 py-1 text-sm" [(ngModel)]="builder.neighborhood.entityLabel" (change)="onEntityLabelChange()">
                          <option [ngValue]="''">Any</option>
                          @for (opt of entityOptions; track opt.value) { <option [ngValue]="opt.value">{{ opt.label }}</option> }
                        </select>
                      </div>
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.START_NODE" | translate}} {{ builder.neighborhood.useName ? 'Name (n.name)' : 'ID (id(n))' }}</label>
                        @if (!builder.neighborhood.useName) {
                          <input class="w-full border rounded px-2 py-1 text-sm" [placeholder]="'NETWORK_ANALYSIS.EG_ID' | translate" [(ngModel)]="builder.neighborhood.id" (input)="onSimpleFieldChange()" />
                        } @else {
                          <div class="relative">
                            <input class="w-full border rounded px-2 py-1 text-sm" [placeholder]="'NETWORK_ANALYSIS.EG_NAME' | translate" [(ngModel)]="builder.neighborhood.name" (input)="onNeighborhoodNameInput()" [disabled]="!builder.neighborhood.entityLabel" />
                            @if(suggestionsLoading) {
                              <div class="absolute right-2 top-1/2 -translate-y-1/2" aria-label="Loading">
                                <span class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></span>
                              </div>
                            }
                            @if(nameSuggestions.length > 0) {
                              <ul class="absolute left-0 right-0 z-50 border border-gray-300 rounded bg-white max-h-40 overflow-auto mt-1 shadow">
                                @for (s of nameSuggestions; track s.elementId) {
                                  <li class="px-2 py-1 hover:bg-blue-50 cursor-pointer flex items-center justify-between" (click)="applyNameSuggestion(s)">
                                    <span class="text-sm font-medium text-gray-800">{{ s.display }}</span>
                                    @if(s.labels?.length) { <span class="text-[11px] text-gray-500 ml-2">{{ s.labels?.join(', ') }}</span> }
                                  </li>
                                }
                              </ul>
                            }
                          </div>
                        }
                      </div>
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.HOPS" | translate}}</label>
                        <input type="number" min="1" max="6" class="w-full border rounded px-2 py-1 text-sm" [(ngModel)]="builder.neighborhood.hops" (input)="onSimpleFieldChange()" />
                      </div>
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.DIRECTION" | translate}}</label>
                        <select class="w-full border rounded px-2 py-1 text-sm" [(ngModel)]="builder.neighborhood.direction" (change)="onSimpleFieldChange()">
                          <option value="any">{{"NETWORK_ANALYSIS.ANY" | translate}}</option>
                          <option value="out">{{"NETWORK_ANALYSIS.OUTGOING" | translate}}</option>
                          <option value="in">{{"NETWORK_ANALYSIS.INCOMING" | translate}}</option>
                        </select>
                      </div>
                      <div class="flex items-center gap-2">
                        <label class="text-xs text-gray-600">{{"NETWORK_ANALYSIS.FAST_MODE" | translate}}</label>
                        <input type="checkbox" class="rounded" [(ngModel)]="builder.neighborhood.fastMode" (change)="onSimpleFieldChange()" />
                      </div>
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.MAX_PATHS" | translate}}</label>
                        <input type="number" min="50" max="5000" class="w-full border rounded px-2 py-1 text-sm" [(ngModel)]="builder.neighborhood.maxPaths" (input)="onSimpleFieldChange()" />
                      </div>
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.MAX_NODES_FAST" | translate}}</label>
                        <input type="number" min="50" max="20000" class="w-full border rounded px-2 py-1 text-sm" [(ngModel)]="builder.neighborhood.maxNodes" (input)="onSimpleFieldChange()" />
                      </div>
                      <div>
                        <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.MAX_RELATIONSHIPS_FAST" | translate}}</label>
                        <input type="number" min="50" max="20000" class="w-full border rounded px-2 py-1 text-sm" [(ngModel)]="builder.neighborhood.maxRels" (input)="onSimpleFieldChange()" />
                      </div>
                      <div class="col-span-2 text-[11px] text-gray-500">{{"NETWORK_ANALYSIS.TIP" | translate}}.</div>
                    </div>
                  }

                  <!-- Other modes removed: only Neighborhood is supported -->
                </div>

                    <!-- Preview panel -->
                <div class="md:col-span-1">
                  <div class="text-xs text-gray-500 mb-1">{{"NETWORK_ANALYSIS.PREVIEW" | translate}}</div>
                  <div class="border border-gray-300 rounded p-2 bg-gray-50">
                    <div class="text-[11px] font-semibold text-gray-700 mb-1">{{"NETWORK_ANALYSIS.CYPHER" | translate}}</div>
                    <pre class="text-[11px] whitespace-pre-wrap font-mono">{{ previewCypher || ('NETWORK_ANALYSIS.FILL_FORM_TO_PREVIEW' | translate) }}</pre>
                  </div>
                </div>
              </div>
              @if (queryError) { <div class="text-sm text-red-600">{{ queryError }}</div> }
            }

            @if (builderTab === 'advanced') {
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.TEMPLATE" | translate}}</label>
                  <select class="w-full border border-gray-300 rounded px-2 py-1 text-sm" (change)="onQueryTemplateChange($any($event.target).selectedIndex)">
                    @for (tpl of queryTemplates; track tpl.label) { <option>{{ tpl.label | translate }}</option> }
                  </select>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.CYPHER" | translate}}</label>
                  <textarea [(ngModel)]="customCypher" rows="6" class="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" [placeholder]="'NETWORK_ANALYSIS.CYPHER_PLACEHOLDER' | translate"></textarea>
                </div>
                <div class="md:col-span-2">
                  <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.PARAMS_JSON" | translate}}</label>
                  <textarea [(ngModel)]="customParamsText" rows="6" class="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" placeholder="{ }"></textarea>
                </div>
                @if (queryError) { <div class="text-sm text-red-600 md:col-span-2">{{ queryError }}</div> }
              </div>
            }

            <div class="relative flex justify-end gap-2 pt-2">
              <button type="button" class="px-3 py-1 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed" (click)="closeQueryBuilder()" [disabled]="isLoading">{{"NETWORK_ANALYSIS.CANCEL" | translate}}</button>
              <button type="button" class="px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2" (click)="applyQueryBuilder()" [disabled]="isLoading">
                <span>{{"NETWORK_ANALYSIS.RUN" | translate}}</span>
                @if(isLoading) {
                  <span class="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                }
              </button>
              @if(isLoading) {
                <div class="absolute inset-0 bg-white/60 rounded pointer-events-none"></div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Current Query Status Bar -->
    @if(currentQueryDescription && !isLoading) {
      <div
        class="absolute left-3 right-3 z-25"
        [style.top.px]="48"
      >
        <div class="bg-blue-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm">
          <div class="flex items-center flex-wrap gap-y-1">
            <svg class="w-4 h-4 mr-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
              <path d="M7 8h10M7 12h6"/>
            </svg>
            <span class="font-semibold text-blue-100">{{"NETWORK_ANALYSIS.ACTIVE_QUERY" | translate}}:</span>
            <span class="ml-3 font-medium">{{ currentQueryDescription | translate }}</span>
            @if (noRelationshipsWarning) {
              <span class="ml-3 px-2 py-0.5 rounded bg-yellow-100 text-yellow-900 text-[10px] font-semibold border border-yellow-200">
                {{ noRelationshipsWarning }}
              </span>
            }
            @if (statusBarNodeLabel) {
              <button
                type="button"
                class="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/15 hover:bg-white/25 focus:outline-none cursor-pointer"
                [title]="statusBarNodeLabel"
                (click)="focusOnStatusBarNode()"
              >
                <svg class="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14" />
                  <path d="M13 5l7 7-7 7" />
                </svg>
              </button>
            }
            <!-- Download dropdown -->
            <div class="relative inline-block ml-auto">
              <button type="button" class="inline-flex items-center gap-1.5 px-3 py-1 rounded text-sm font-medium bg-white/15 hover:bg-white/25 text-white transition-colors" [ngClass]="{ 'bg-white/25': showDownloadDropdown }" (click)="toggleDownloadDropdown()">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span>{{ "NETWORK_ANALYSIS.DOWNLOAD" | translate }}</span>
                <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
              @if (showDownloadDropdown) {
                <div class="absolute top-full right-0 mt-1 w-56 bg-white border border-gray-300 rounded shadow-lg z-50">
                  <button type="button"
                    class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    [disabled]="!lastRawResult"
                    (click)="downloadLastRawResult(); toggleDownloadDropdown()"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    {{"NETWORK_ANALYSIS.DOWNLOAD_RAW_RESULT" | translate}}
                  </button>
                  <button type="button"
                    class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    [disabled]="!lastExtractedGraph"
                    (click)="downloadLastGraph(); toggleDownloadDropdown()"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="2"/>
                      <circle cx="19" cy="5" r="2"/>
                      <circle cx="5" cy="19" r="2"/>
                      <line x1="13.5" y1="10.5" x2="17.5" y2="6.5"/>
                      <line x1="10.5" y1="13.5" x2="6.5" y2="17.5"/>
                    </svg>
                    {{"NETWORK_ANALYSIS.DOWNLOAD_EXTRACTED_GRAPH" | translate}}
                  </button>
                  <button type="button"
                    class="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    [disabled]="!lastRawResult"
                    (click)="downloadBrowserStyleRecords(); toggleDownloadDropdown()"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="3" y="3" width="7" height="7"/>
                      <rect x="14" y="3" width="7" height="7"/>
                      <rect x="14" y="14" width="7" height="7"/>
                      <rect x="3" y="14" width="7" height="7"/>
                    </svg>
                    {{"NETWORK_ANALYSIS.DOWNLOAD_RECORDS" | translate}}
                  </button>
                </div>
              }
            </div>
          </div>
        </div>
      </div>
    }

    @if (noRelationshipsWarning && !isLoading && !currentQueryDescription) {
      <div class="absolute left-3 right-3 z-25" [style.top.px]="48">
        <div class="bg-yellow-50 text-yellow-900 border border-yellow-300 px-4 py-2 rounded-lg shadow-sm text-xs">
          {{ noRelationshipsWarning }}
        </div>
      </div>
    }

    <!-- Top-right Graph Info window under the toolbar -->
    @if (showGraphInfoPanel) {
    <div
      class="absolute left-3 z-20"
      [style.top.px]="currentQueryDescription && !isLoading ? 96 : 48"
      [class.opacity-0]="isLoading"
      [class.opacity-100]="!isLoading"
      [class.transition-opacity]="true"
    >
      <div class="bg-white/95 border border-gray-300 rounded shadow-sm px-3 py-2 text-xs text-gray-800 min-w-[220px] max-w-[380px]">
        <div class="flex items-center justify-between">
          <div class="font-semibold">{{"NETWORK_ANALYSIS.GRAPH_INFO" | translate}}</div>
          <div class="text-[10px] text-gray-500">{{"NETWORK_ANALYSIS.LIVE" | translate}}</div>
        </div>
        @if(currentQueryDescription) {
          <div class="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
            <div class="text-blue-700 font-semibold text-xs mb-1 flex items-center">
              <svg class="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
                <path d="M7 8h10M7 12h6"/>
              </svg>
              {{"NETWORK_ANALYSIS.CURRENT_QUERY" | translate}}:
            </div>
            <div class="text-xs text-blue-800 font-medium leading-snug">{{ currentQueryDescription | translate }}</div>
          </div>
        }
        <div class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1">
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.NODES" | translate}}</div>
          <div class="text-right font-medium">{{ nodeCount }}</div>
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.LINKS" | translate}}</div>
          <div class="text-right font-medium">{{ linkCount }}</div>
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.HIGHLIGHTED" | translate}}</div>
          <div class="text-right">{{ highlightedNodeCount }}N / {{ highlightedLinkCount }}L</div>
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.NODES_LABELS" | translate}}</div>
          <div class="text-right">{{ showLabels ? ('NETWORK_ANALYSIS.ON' | translate) : ('NETWORK_ANALYSIS.OFF' | translate) }}</div>
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.LINK_LABELS" | translate}}</div>
          <div class="text-right">{{ showLinkLabels ? ('NETWORK_ANALYSIS.ON' | translate) : ('NETWORK_ANALYSIS.OFF' | translate) }}</div>
        </div>
      </div>
    </div>
    }

    <!-- Highlight status indicator -->
    <div
      class="absolute right-3 bg-white bg-opacity-90 rounded p-2 z-20 text-gray-800 text-sm border border-gray-300 shadow-sm"
      [style.top.px]="headerOffset + 24"
      [class.opacity-0]="isLoading || (!isHighlightActive())"
      [class.opacity-100]="!isLoading && isHighlightActive()"
      [class.transition-opacity]="true"
    >
      <div class="flex items-center gap-2">
        <div class="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
        <span>{{ getHighlightStatus() }}</span>
        <button
          class="ml-2 px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded text-xs"
          (click)="clearHighlights()"
        >
          {{"NETWORK_ANALYSIS.CLEAR" | translate}}
        </button>
      </div>
    </div>
    <!-- Selected info overlay (positioned below query status bar) -->
  @if (showSelectedPanel && filteredSelected; as selected) {
  <div class="absolute right-3 bg-white bg-opacity-95 text-gray-800 text-xs rounded p-3 z-20 max-w-[420px] max-h-[260px] overflow-auto shadow-md border border-gray-300" [style.top.px]="currentQueryDescription && !isLoading ? 96 : 48">
      <div class="flex items-center justify-between mb-2">
        <div class="font-semibold text-sm">{{ selected.type ? ('NETWORK_ANALYSIS.LINK' | translate) : ('NETWORK_ANALYSIS.NODE' | translate) }} {{"NETWORK_ANALYSIS.DETAILS" | translate}}</div>
        <button class="text-gray-500 hover:text-gray-800" (click)="clearSelection()">&times;</button>
      </div>

      <!-- Node Details -->
      @if (!selected.type) {
        <div class="grid grid-cols-3 gap-x-3 gap-y-2 items-center">
          <button
            type="button"
            class="col-span-3 text-left font-semibold text-sm truncate text-blue-700 hover:text-blue-900 hover:underline focus:outline-none"
            [title]="selectedNodeName"
            (click)="focusOnSelectedNode()"
          >
            {{ selectedNodeName }}
          </button>
          
          <!-- Basic Info -->
          <div class="text-gray-500 text-xs">{{"NETWORK_ANALYSIS.ID" | translate}}</div>
          <div class="col-span-2 font-mono text-gray-700 text-xs">{{ selectedNodeActualId }}</div>
          
          <div class="text-gray-500 text-xs">{{"NETWORK_ANALYSIS.LABELS" | translate}}</div>
          <div class="col-span-2 text-xs">{{ selectedNode.labels?.join(', ') }}</div>
          
          <!-- Aggregate Weight (sum of connected link weights) -->
          @if (selectedNodeAggregateWeight !== null) {
            <div class="col-span-3 border-t border-gray-200 pt-1 mt-1"></div>
            <div class="text-gray-600 text-xs font-semibold">{{ 'NETWORK_ANALYSIS.AGGREGATE_WEIGHT' | translate }}</div>
            <div class="col-span-2 text-lg font-bold" [class.text-blue-700]="weightMode === 'frequency'" [class.text-orange-700]="weightMode === 'cost'">{{ selectedNodeAggregateWeight | number:'1.0-1' }}</div>
          }
          
          <!-- Active Community -->
          @if (selectedNodeActiveCommunity !== null) {
            <div class="col-span-1 text-gray-600 text-xs font-semibold">{{ 'NETWORK_ANALYSIS.COMMUNITY' | translate }}</div>
            <div class="col-span-2 text-sm font-semibold text-purple-700">{{ selectedNodeActiveCommunity }}</div>
          }

          
          <!-- Connections Count (Degree as weight metric) -->
          @if (selectedNodeDegreeMetric !== null) {
            <div class="text-gray-600 text-xs font-semibold">{{"NETWORK_ANALYSIS.CONNECTIONS" | translate}}</div>
            <div class="col-span-2 text-lg font-semibold text-gray-800">{{ selectedNodeDegreeMetric }}</div>
          } @else {
            <div class="text-gray-600 text-xs font-semibold">{{"NETWORK_ANALYSIS.CONNECTIONS" | translate}}</div>
            <div class="col-span-2 text-lg font-semibold text-gray-800">{{ selectedNodeDegree }}</div>
          }
          
        </div>
      }

      <!-- Link Details -->
      @if (selected.type) {
        <div class="grid grid-cols-3 gap-x-3 gap-y-1">
          <div class="col-span-3 font-medium text-base">{{ selected.type }}</div>
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.SOURCE" | translate}}</div><div class="col-span-2 font-mono text-gray-700">{{ selected.source?.id ?? selected.source }}</div>
          <div class="text-gray-500">{{"NETWORK_ANALYSIS.TARGET" | translate}}</div><div class="col-span-2 font-mono text-gray-700">{{ selected.target?.id ?? selected.target }}</div>
          @if (selectedLinkActiveWeight !== null) {
            <div class="text-gray-500">{{ activeWeightLabelKey | translate }}</div>
            <div class="col-span-2 font-semibold" [class.text-blue-700]="weightMode === 'frequency'" [class.text-orange-700]="weightMode === 'cost'">{{ selectedLinkActiveWeight | number:'1.0-2' }}</div>
          }
        </div>
      }

      <!-- All properties table -->
      @if (selected.properties && objectKeys(selected.properties).length > 0) {
        <div class="mt-3 pt-2 border-t border-gray-200">
          <div class="font-semibold mb-1">{{"NETWORK_ANALYSIS.PROPERTIES" | translate}}</div>
          <div class="grid grid-cols-3 gap-x-3 gap-y-1 max-h-[80px] overflow-y-auto">
            @for (key of objectKeys(selected.properties); track key) {
              <div class="text-gray-500 truncate" [title]="key">{{ key }}</div>
              <div class="col-span-2 font-mono text-gray-700 truncate" [title]="selected.properties[key]">{{ selected.properties[key] }}</div>
            }
          </div>
        </div>
      }
    </div>
  }
</div>

<!-- Right-side Drawer (non-blocking) with slide animation -->
<div class="fixed right-0 bottom-0 bg-white border-l border-gray-300 shadow-xl z-[9999] flex flex-col box-border transform transition-transform duration-300 ease-in-out"
  [class.translate-x-0]="isSettingsOpen" [class.translate-x-full]="!isSettingsOpen"
  [attr.aria-hidden]="!isSettingsOpen"
  [style.top.px]="headerOffset"
  [style.width.px]="360"
  [style.maxWidth]="'100vw'">
  <div class="flex items-center justify-between px-4 py-3 border-b bg-white sticky top-0 z-10">
    <h3 class="text-base font-semibold">{{"NETWORK_ANALYSIS.GRAPH_CONTROLS" | translate}}</h3>
    <button
      type="button"
      [attr.aria-label]="'NETWORK_ANALYSIS.CLOSE_DRAWER' | translate"
      [title]="'NETWORK_ANALYSIS.CLOSE' | translate"
      class="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
      (click)="closeSettings()"
    >
      ✕
    </button>
  </div>
  <div class="p-4 flex-1 overflow-y-auto">
  
  
  
  <!-- Visual Settings -->
  <div class="mb-4">
    <h4 class="font-semibold text-sm mb-2 text-gray-700">{{"NETWORK_ANALYSIS.VISUAL_SETTINGS" | translate}}</h4>
    
    
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.NODE_SIZE" | translate}}: {{settings.nodeRelSize}}</label>
      <input type="range" min="1" max="20" step="1" 
             [(ngModel)]="settings.nodeRelSize"
             (input)="updateNodeSize()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.LINK_WIDTH" | translate}}: {{settings.linkWidth}}</label>
      <input type="range" min="0.1" max="5" step="0.1" 
             [(ngModel)]="settings.linkWidth"
             (input)="updateLinkWidth()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.ARROW_SIZE" | translate}}: {{settings.arrowSize}}</label>
      <input type="range" min="0" max="12" step="0.5" 
             [(ngModel)]="settings.arrowSize"
             (input)="updateArrowSize()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.LINK_OPACITY" | translate}}: {{settings.linkOpacity}}</label>
      <input type="range" min="0.1" max="1" step="0.1" 
             [(ngModel)]="settings.linkOpacity"
             (input)="updateLinkOpacity()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
  </div>
  
  <!-- Particle Settings -->
  <div class="mb-4">
    <h4 class="font-semibold text-sm mb-2 text-gray-700">{{"NETWORK_ANALYSIS.PARTICLE_EFFECTS" | translate}}</h4>
    <p class="text-xs text-gray-500 mb-3">{{"NETWORK_ANALYSIS.MOVING_PARTICLES_APPEAR" | translate}}</p>
    
    <div class="mb-3">
      <label class="flex items-center text-xs text-gray-600 cursor-pointer">
        <input
          type="checkbox"
          [(ngModel)]="settings.enableParticles"
          (change)="updateParticleEnabled()"
          class="mr-2 rounded"
        />
        {{"NETWORK_ANALYSIS.ENABLE_MOVING_PARTICLES" | translate}}
      </label>
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.PARTICLE_SPEED" | translate}}: {{settings.particleSpeed}}</label>
      <input type="range" min="0.001" max="0.02" step="0.001" 
             [(ngModel)]="settings.particleSpeed"
             (input)="updateParticleSpeed()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.PARTICLE_SIZE" | translate}}: {{settings.particleWidth}}</label>
      <input type="range" min="4" max="10" step="0.5" 
             [(ngModel)]="settings.particleWidth"
             (input)="updateParticleWidth()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.PARTICLES_PER_LINK" | translate}}: {{settings.particleDensity}}</label>
      <input type="range" min="1" max="10" step="1" 
             [(ngModel)]="settings.particleDensity"
             (input)="updateParticleDensity()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
  </div>
  
  <!-- Physics Settings -->
  <div class="mb-4">
    <h4 class="font-semibold text-sm mb-2 text-gray-700">{{"NETWORK_ANALYSIS.PHYSICS_SETTINGS" | translate}}</h4>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.LINK_DISTANCE" | translate}}: {{settings.linkDistance}}</label>
      <input type="range" min="5" max="200" step="5" 
             [(ngModel)]="settings.linkDistance"
             (input)="updateLinkDistance()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.LINK_STRENGTH" | translate}}: {{settings.linkStrength}}</label>
      <input type="range" min="0" max="2" step="0.1" 
             [(ngModel)]="settings.linkStrength"
             (input)="updateLinkStrength()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.CHARGE_STRENGTH" | translate}}: {{settings.chargeStrength}}</label>
      <input type="range" min="-500" max="0" step="10" 
             [(ngModel)]="settings.chargeStrength"
             (input)="updateChargeStrength()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.CHARGE_DISTANCE" | translate}}: {{settings.chargeDistance}}</label>
      <input type="range" min="50" max="500" step="10" 
             [(ngModel)]="settings.chargeDistance"
             (input)="updateChargeDistance()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.CENTER_STRENGTH" | translate}}: {{settings.centerStrength}}</label>
      <input type="range" min="0" max="0.1" step="0.01" 
             [(ngModel)]="settings.centerStrength"
             (input)="updateCenterStrength()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
  </div>

  
  <!-- Collision Settings -->
  <div class="mb-4">
    <h4 class="font-semibold text-sm mb-2 text-gray-700">{{"NETWORK_ANALYSIS.COLLISION_SETTINGS" | translate}}</h4>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.COLLISION_RADIUS" | translate}}: {{settings.collisionRadius}}</label>
      <input type="range" min="1" max="10" step="0.5" 
             [(ngModel)]="settings.collisionRadius"
             (input)="updateCollisionRadius()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
    
    <div class="mb-3">
      <label class="block text-xs text-gray-600 mb-1">{{"NETWORK_ANALYSIS.COLLISION_STRENGTH" | translate}}: {{settings.collisionStrength}}</label>
      <input type="range" min="0" max="1" step="0.1" 
             [(ngModel)]="settings.collisionStrength"
             (input)="updateCollisionStrength()"
             class="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer">
    </div>
  </div>
  
  <!-- Preset Buttons -->
  <div class="mb-4">
    <h4 class="font-semibold text-sm mb-2 text-gray-700">{{"NETWORK_ANALYSIS.PRESETS" | translate}}</h4>
    <button (click)="applyPreset('tight')" 
            class="w-full mb-2 px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600">
      {{"NETWORK_ANALYSIS.TIGHT_CLUSTERS" | translate}}
    </button>
    <button (click)="applyPreset('spread')" 
            class="w-full mb-2 px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600">
      {{"NETWORK_ANALYSIS.SPREAD_OUT" | translate}}
    </button>
    <button (click)="applyPreset('hub')" 
            class="w-full mb-2 px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600">
      {{"NETWORK_ANALYSIS.HUB_PATTERN" | translate}}
    </button>
    <button (click)="applyPreset('default')" 
            class="w-full px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600">
      {{"NETWORK_ANALYSIS.RESET_DEFAULT" | translate}}
    </button>
  </div>
  
  <!-- Help Section -->
  <div class="mt-auto pt-4 border-t border-gray-300">
    <h4 class="font-semibold text-xs mb-2 text-gray-700">{{"NETWORK_ANALYSIS.INTERACTION_HELP" | translate}}</h4>
    <div class="text-xs text-gray-600 space-y-1">
      <div>• <strong>{{"NETWORK_ANALYSIS.CLICK" | translate}}</strong> {{"NETWORK_ANALYSIS.NODES_LINKS_TO_HIGHLIGHT" | translate}}</div>
      <div>• <strong>{{"NETWORK_ANALYSIS.CLICK_BACKGROUND" | translate}}</strong> {{"NETWORK_ANALYSIS.TO_CLEAR" | translate}}</div>
      <div>• <strong>{{"NETWORK_ANALYSIS.DRAG" | translate}}</strong> {{"NETWORK_ANALYSIS.NODES_TO_MOVE" | translate}}</div>
      <div>• <strong>{{"NETWORK_ANALYSIS.SCROLL" | translate}}</strong> {{"NETWORK_ANALYSIS.TO_ZOOM" | translate}}</div>
      <div>• <strong>{{"NETWORK_ANALYSIS.ESC_KEY" | translate}}</strong> {{"NETWORK_ANALYSIS.TO_CLEAR_HIGHLIGHTS" | translate}}</div>
      <div>• <strong>{{"NETWORK_ANALYSIS.PARTICLES" | translate}}</strong> {{"NETWORK_ANALYSIS.SHOW_FLOW_ON_HIGHLIGHTS_LINKS" | translate}}</div>
    </div>
      <div class="mt-4">
        <h5 class="font-semibold text-xs mb-2 text-gray-700">{{"NETWORK_ANALYSIS.DEBUG_EXPORT" | translate}}</h5>
        <label class="flex items-center text-xs text-gray-600 mb-2">
          <input type="checkbox" class="mr-2" [(ngModel)]="neo4jDebug" /> {{"NETWORK_ANALYSIS.ENABLE_DEBUG_LOGS" | translate}}
        </label>
        <div class="flex gap-2">
          <button (click)="downloadLastRawResult()"
                  class="px-3 py-1 bg-gray-200 text-gray-800 text-xs rounded hover:bg-gray-300 border border-gray-300">
            {{"NETWORK_ANALYSIS.DOWNLOAD_RAW_RESULT" | translate}}
          </button>
          <button (click)="downloadLastGraph()"
                  class="px-3 py-1 bg-gray-200 text-gray-800 text-xs rounded hover:bg-gray-300 border border-gray-300">
            {{"NETWORK_ANALYSIS.DOWNLOAD_EXTRACTED_GRAPH" | translate}}
          </button>
          <button (click)="downloadBrowserStyleRecords()"
                  class="px-3 py-1 bg-gray-200 text-gray-800 text-xs rounded hover:bg-gray-300 border border-gray-300">
            {{"NETWORK_ANALYSIS.DOWNLOAD_RECORDS" | translate}}
          </button>
        </div>
      </div>
  </div>
  </div>
</div>

<!-- Help Drawer -->
<div class="fixed right-0 bottom-0 bg-white border-l border-gray-300 shadow-xl z-[9998] flex flex-col box-border transform transition-transform duration-300 ease-in-out"
  [class.translate-x-0]="isHelpOpen" [class.translate-x-full]="!isHelpOpen"
  [attr.aria-hidden]="!isHelpOpen"
  [style.top.px]="headerOffset"
  [style.width.px]="400"
  [style.maxWidth]="'100vw'">
  <div class="flex items-center justify-between px-4 py-3 border-b bg-white sticky top-0 z-10">
    <h3 class="text-base font-semibold">{{"NETWORK_ANALYSIS.HELP" | translate}}</h3>
    <button
      type="button"
      [attr.aria-label]="'NETWORK_ANALYSIS.CLOSE_DRAWER' | translate"
      [title]="'NETWORK_ANALYSIS.CLOSE' | translate"
      class="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
      (click)="closeHelp()"
    >
      ✕
    </button>
  </div>
  <div class="p-4 flex-1 overflow-y-auto space-y-4">
    <div class="space-y-2">
      <div class="text-sm font-semibold">{{"NETWORK_ANALYSIS.NODE_COLORS" | translate}}</div>
      <div class="text-xs text-gray-700">
        {{"NETWORK_ANALYSIS.NODE_COLORS_DESC" | translate}}
      </div>
      <div class="grid grid-cols-2 gap-2 text-xs text-gray-700">
        <div class="flex items-center gap-2">
          <span class="w-2.5 h-2.5 rounded-full" style="background:#4ecdc4"></span>
          <span>{{"NETWORK_ANALYSIS.NODE_TYPE_PROVIDER" | translate}}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-2.5 h-2.5 rounded-full" style="background:#ff6b6b"></span>
          <span>{{"NETWORK_ANALYSIS.NODE_TYPE_DOCTOR" | translate}}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-2.5 h-2.5 rounded-full" style="background:#feca57"></span>
          <span>{{"NETWORK_ANALYSIS.NODE_TYPE_AGENT" | translate}}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-2.5 h-2.5 rounded-full" style="background:#45b7d1"></span>
          <span>{{"NETWORK_ANALYSIS.NODE_TYPE_CUSTOMER" | translate}}</span>
        </div>
      </div>
    </div>

    <div class="space-y-2">
      <div class="text-sm font-semibold">{{"NETWORK_ANALYSIS.NODE_SIZE" | translate}}</div>
      <div class="text-xs text-gray-700">
        @if (weightMode === 'frequency') {
          {{"NETWORK_ANALYSIS.NODE_SIZE_FREQUENCY" | translate}}
        } @else {
          {{"NETWORK_ANALYSIS.NODE_SIZE_COST" | translate}}
        }
      </div>
    </div>

    <div class="space-y-2">
      <div class="text-sm font-semibold">{{"NETWORK_ANALYSIS.LINK_COLORS" | translate}}</div>
      <div class="text-xs text-gray-700">
        {{"NETWORK_ANALYSIS.LINK_COLORS_DESC" | translate}}
      </div>
      <div class="mt-2">
        <div class="text-xs font-semibold text-gray-700 mb-1">{{"NETWORK_ANALYSIS.WEIGHT" | translate}}</div>
        <div class="flex items-center gap-2">
          <span class="text-[10px] text-gray-500">{{"NETWORK_ANALYSIS.LOW" | translate}}</span>
          <div class="w-32 h-2 rounded" style="background: linear-gradient(90deg, rgba(0,0,255,0.7) 0%, rgba(0,255,255,0.7) 25%, rgba(0,255,0,0.7) 50%, rgba(255,255,0,0.7) 75%, rgba(255,0,0,0.7) 100%);"></div>
          <span class="text-[10px] text-gray-500">{{"NETWORK_ANALYSIS.HIGH" | translate}}</span>
        </div>
      </div>
    </div>

    <div class="space-y-2">
      <div class="text-sm font-semibold">{{"NETWORK_ANALYSIS.LINK_DISTANCE" | translate}}</div>
      <div class="text-xs text-gray-700">
        {{"NETWORK_ANALYSIS.LINK_DISTANCE_DESC" | translate}}
      </div>
    </div>

    <div class="space-y-2">
      <div class="text-sm font-semibold">{{"NETWORK_ANALYSIS.TOOLBAR_ICONS" | translate}}</div>
      <ul class="text-xs text-gray-700 space-y-2">
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
            <path d="M7 8h10M7 12h6"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.OPEN_QUERY_BUILDER" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_QUERY_BUILDER_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            <line x1="6.5" y1="17.5" x2="6.51" y2="17.5"/>
            <line x1="6.5" y1="6.5" x2="6.51" y2="6.5"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.OPEN_NETWORK_TOOLS" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_NETWORK_TOOLS_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="7"/>
            <line x1="11" y1="8" x2="11" y2="14"/>
            <line x1="8" y1="11" x2="14" y2="11"/>
            <line x1="16.5" y1="16.5" x2="21" y2="21"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.ZOOM_IN" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_ZOOM_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 17v5m-3-2l3-3 3 3M9 2v7.586a2 2 0 0 1-.586 1.414l-2.828 2.828A2 2 0 0 0 5 15.242V19h14v-3.758a2 2 0 0 0-.586-1.414l-2.828-2.828A2 2 0 0 1 15 9.586V2H9z"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.PIN_AFTER_DRAG" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_PIN_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 10L12 2H4v8l8 8 8-8z"/>
            <circle cx="7" cy="7" r="1.5"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.SHOW_NODE_LABELS" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_NODE_LABELS_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1"/>
            <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.SHOW_LINK_LABELS" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_LINK_LABELS_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="8"/>
            <path d="M10 12h2v4"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.SHOW_GRAPH_INFO" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_GRAPH_INFO_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="4" width="18" height="14" rx="2" ry="2"/>
            <line x1="7" y1="8" x2="17" y2="8"/>
            <line x1="7" y1="12" x2="13" y2="12"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.SHOW_SELECTED_DETAILS" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_SELECTED_DETAILS_DESC" | translate}}</span>
        </li>
        <li class="flex items-start gap-2">
          <svg class="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c0 .67.39 1.27 1 1.51H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
          <span><strong>{{"NETWORK_ANALYSIS.OPEN_SETTINGS" | translate}}:</strong> {{"NETWORK_ANALYSIS.TOOLBAR_SETTINGS_DESC" | translate}}</span>
        </li>
      </ul>
    </div>

    <div class="space-y-2">
      <div class="text-sm font-semibold">{{"NETWORK_ANALYSIS.INTERACTION_HELP" | translate}}</div>
      <ul class="text-xs text-gray-700 list-disc pl-4 space-y-1">
        <li>{{"NETWORK_ANALYSIS.TIP_CLICK_NODE" | translate}}</li>
        <li>{{"NETWORK_ANALYSIS.TIP_DRAG_NODE" | translate}}</li>
        <li>{{"NETWORK_ANALYSIS.TIP_SCROLL_ZOOM" | translate}}</li>
      </ul>
    </div>
  </div>
</div>

